import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { staticFeatureFlags } from '@/config/features';

export const THEMES = ['dark', 'light', 'system'] as const;
export type Theme = (typeof THEMES)[number];
export type InferredTheme = Extract<Theme, 'dark' | 'light'>;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  inferredTheme: InferredTheme;
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const DEFAULT_STORAGE_KEY = 'theme';

const DEFAULT_THEME = 'light';

const initialState: ThemeProviderState = {
  inferredTheme: DEFAULT_THEME,
  theme: DEFAULT_THEME,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

const getSystemTheme = () => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

const getInitialTheme = (storageKey: string, defaultTheme: Theme): Theme => {
  if (!staticFeatureFlags.THEME_SELECTOR) return defaultTheme;

  const theme = localStorage.getItem(storageKey);

  if (THEMES.includes(theme as Theme)) return theme as Theme;

  return defaultTheme;
};

export const ThemeProvider = ({ children, defaultTheme = DEFAULT_THEME, storageKey = DEFAULT_STORAGE_KEY, ...props }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme(storageKey, defaultTheme));
  const inferredTheme = theme === 'system' ? getSystemTheme() : theme;

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      root.classList.add(getSystemTheme());

      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    inferredTheme,
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
};
ThemeProvider.displayName = 'ThemeProvider';

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (!context) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
